import { useState, useEffect } from 'react';
import { businessIntelligenceAPI } from '../lib/api';
import {
  FiArchive, FiPlus, FiTrash2, FiLoader, FiAlertCircle, FiCheck,
  FiCloud, FiMail, FiFolder, FiToggleLeft, FiToggleRight, FiDatabase,
  FiChevronDown, FiChevronRight, FiChevronUp, FiEdit2, FiX,
} from 'react-icons/fi';
import ArchiveFolderBrowser from './ArchiveFolderBrowser';
import BISchedulerSettings from './BISchedulerSettings';

const ARCHIVE_DOC_TYPES = [
  { value: 'mails', label: 'Mails' },
  { value: 'attachments', label: 'Attachments' },
  { value: 'static_documents', label: 'Static Documents (Read Only)' },
  { value: 'editable_documents', label: 'Editable Documents' },
  { value: 'photos', label: 'Photos' },
  { value: 'images', label: 'Images' },
];

const DOC_TYPE_PLACEHOLDERS = {
  mails: 'Email messages received or sent, stored for compliance or reference',
  attachments: 'Files attached to emails or documents, archived alongside their parent',
  static_documents: 'Read-only reference documents that should not be modified',
  editable_documents: 'Working documents that may be edited after archiving',
  photos: 'Photographic images captured on-site or submitted for record-keeping',
  images: 'Scanned images, screenshots, or other digital image files',
};

function typeLabel(value) {
  return ARCHIVE_DOC_TYPES.find(t => t.value === value)?.label || value;
}

function typeColor(value) {
  const map = {
    mails: 'bg-purple-100 text-purple-700',
    attachments: 'bg-blue-100 text-blue-700',
    static_documents: 'bg-gray-100 text-gray-700',
    editable_documents: 'bg-green-100 text-green-700',
    photos: 'bg-pink-100 text-pink-700',
    images: 'bg-teal-100 text-teal-700',
  };
  return map[value] || 'bg-gray-100 text-gray-700';
}

function mappingPath(m) {
  if (m.source === 'sharepoint') {
    return m.folder_path || [m.site_name, m.drive_name, m.folder_name].filter(Boolean).join(' / ');
  }
  if (m.source === 'doxolve') {
    return m.folder_path || m.folder_name || 'Root';
  }
  return [m.mailbox_email, m.mail_folder_name].filter(Boolean).join(' / ');
}

function SourceIcon({ source }) {
  if (source === 'sharepoint') return <FiCloud className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />;
  if (source === 'doxolve') return <FiDatabase className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />;
  return <FiMail className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />;
}

function DeleteGroupConfirm({ group, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <FiTrash2 className="w-4 h-4" />
            <h2 className="text-sm font-semibold">Delete Group</h2>
          </div>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600">
            <FiX className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-700">
            Delete group <span className="font-semibold">"{group.name}"</span>?
            {group.folder_mappings?.length > 0 && (
              <> This will remove all {group.folder_mappings.length} folder mapping{group.folder_mappings.length !== 1 ? 's' : ''} in this group.</>
            )}
          </p>
          <p className="text-xs text-gray-500">Existing archive data will not be affected.</p>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onCancel} disabled={deleting} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BusinessIntelligenceSettings() {
  const [enabled, setEnabled] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [docTypeDescriptions, setDocTypeDescriptions] = useState({});
  const [savingDocTypes, setSavingDocTypes] = useState(false);
  const [editingDocTypes, setEditingDocTypes] = useState(false);
  const [docTypeEdits, setDocTypeEdits] = useState({});

  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [activeTab, setActiveTab] = useState('groups');

  // { groupId } when the folder browser is open
  const [addFolderToGroup, setAddFolderToGroup] = useState(null);

  // { groupId } when the archive destination picker is open
  const [pickingDestination, setPickingDestination] = useState(null);

  const [editingGroup, setEditingGroup] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [deletingGroupId, setDeletingGroupId] = useState(null);

  const [customTypeState, setCustomTypeState] = useState({});
  const [editingCustomType, setEditingCustomType] = useState(null); // { groupId, typeId, name, description, group_name }
  const [savingCustomTypeEdit, setSavingCustomTypeEdit] = useState(false);

  // { groupId, mappingId, types: [] } when editing a mapping's doc types inline
  const [editingMappingTypes, setEditingMappingTypes] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [configRes, groupsRes] = await Promise.all([
        businessIntelligenceAPI.getConfig(),
        businessIntelligenceAPI.getGroups(),
      ]);
      setEnabled(configRes.data.enabled || false);
      setGroups(groupsRes.data.groups || []);
      setDocTypeDescriptions(configRes.data.doc_type_descriptions || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleToggleEnabled = async () => {
    const newValue = !enabled;
    setEnabled(newValue);
    setSaving(true);
    try {
      await businessIntelligenceAPI.saveConfig({ enabled: newValue });
      showSuccessMsg('Settings saved.');
    } catch (err) {
      setEnabled(!newValue);
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditDocTypes = () => {
    const edits = { ...DOC_TYPE_PLACEHOLDERS };
    Object.entries(docTypeDescriptions).forEach(([k, v]) => { if (v) edits[k] = v; });
    setDocTypeEdits(edits);
    setEditingDocTypes(true);
  };

  const handleCancelDocTypes = () => {
    setEditingDocTypes(false);
    setDocTypeEdits({});
  };

  const handleSaveDocTypes = async () => {
    setSavingDocTypes(true);
    try {
      const res = await businessIntelligenceAPI.saveConfig({ doc_type_descriptions: docTypeEdits });
      setDocTypeDescriptions(res.data.doc_type_descriptions || {});
      setEditingDocTypes(false);
      setDocTypeEdits({});
      showSuccessMsg('Document type descriptions saved.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingDocTypes(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const res = await businessIntelligenceAPI.createGroup({ name: newGroupName.trim(), description: newGroupDesc.trim() || null });
      const g = res.data.group;
      setGroups(prev => [...prev, g].sort((a, b) => a.name.localeCompare(b.name)));
      setExpandedGroups(prev => new Set([...prev, g.id]));
      setNewGroupName('');
      setNewGroupDesc('');
      setShowNewGroupForm(false);
      showSuccessMsg(`Group "${g.name}" created.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAddMapping = async (mapping) => {
    const { groupId } = addFolderToGroup;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = [...(group.folder_mappings || []), mapping];
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { folder_mappings: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setAddFolderToGroup(null);
      showSuccessMsg('Folder added.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setAddFolderToGroup(null);
    }
  };

  const handleRemoveMapping = async (groupId, mappingId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = group.folder_mappings.filter(m => m.id !== mappingId);
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { folder_mappings: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleToggleRfq = async (group) => {
    try {
      const res = await businessIntelligenceAPI.updateGroup(group.id, { rfq_enabled: !group.rfq_enabled });
      setGroups(prev => prev.map(g => g.id === group.id ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSaveMappingTypes = async () => {
    if (!editingMappingTypes) return;
    const { groupId, mappingId, types } = editingMappingTypes;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = group.folder_mappings.map(m =>
      m.id === mappingId ? { ...m, types, type: types[0] || m.type } : m
    );
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { folder_mappings: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setEditingMappingTypes(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSetDestination = async (mapping) => {
    const { groupId } = pickingDestination;
    const destination = {
      drive_id: mapping.drive_id,
      folder_id: mapping.folder_id || null,
      folder_name: mapping.folder_name || mapping.drive_name,
      folder_path: mapping.folder_path || [mapping.site_name, mapping.drive_name, mapping.folder_name].filter(Boolean).join(' / '),
    };
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { archive_destination: destination });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setPickingDestination(null);
      showSuccessMsg('Archive destination saved.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setPickingDestination(null);
    }
  };

  const handleClearDestination = async (groupId) => {
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { archive_destination: null });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      showSuccessMsg('Archive destination cleared.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSaveGroupEdit = async () => {
    if (!editingGroup || !editingGroup.name.trim()) return;
    setEditSaving(true);
    try {
      const res = await businessIntelligenceAPI.updateGroup(editingGroup.id, {
        name: editingGroup.name.trim(),
        description: editingGroup.description || null,
      });
      setGroups(prev => prev.map(g => g.id === editingGroup.id ? res.data.group : g).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingGroup(null);
      showSuccessMsg('Group updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirmDeleteGroup) return;
    setDeletingGroupId(confirmDeleteGroup.id);
    try {
      await businessIntelligenceAPI.deleteGroup(confirmDeleteGroup.id);
      setGroups(prev => prev.filter(g => g.id !== confirmDeleteGroup.id));
      setConfirmDeleteGroup(null);
      showSuccessMsg('Group deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeletingGroupId(null);
    }
  };

  const getCustomTypeState = (groupId) => customTypeState[groupId] || { adding: false, newName: '', newDesc: '', newGroup: '', newRequired: false, saving: false };

  const setCustomTypeField = (groupId, field, value) => {
    setCustomTypeState(prev => ({ ...prev, [groupId]: { ...getCustomTypeState(groupId), ...prev[groupId], [field]: value } }));
  };

  const handleAddCustomType = async (groupId) => {
    const st = getCustomTypeState(groupId);
    const name = st.newName.trim();
    if (!name) return;
    setCustomTypeField(groupId, 'saving', true);
    try {
      const group = groups.find(g => g.id === groupId);
      const existing = group?.custom_doc_types || [];
      const newType = { id: `custom_${Date.now()}`, name, description: st.newDesc.trim() || '', group_name: st.newGroup?.trim() || '', required: !!st.newRequired };
      const updated = [...existing, newType];
      const res = await businessIntelligenceAPI.updateGroup(groupId, { custom_doc_types: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setCustomTypeState(prev => ({ ...prev, [groupId]: { adding: false, newName: '', newDesc: '', newGroup: '', newRequired: false, saving: false } }));
      showSuccessMsg(`Custom type "${name}" added.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setCustomTypeField(groupId, 'saving', false);
    }
  };

  const handleDeleteCustomType = async (groupId, typeId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = (group.custom_doc_types || []).filter(t => t.id !== typeId);
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { custom_doc_types: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSaveCustomTypeEdit = async () => {
    if (!editingCustomType || !editingCustomType.name.trim()) return;
    const { groupId, typeId } = editingCustomType;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    setSavingCustomTypeEdit(true);
    try {
      const updated = (group.custom_doc_types || []).map(t => t.id === typeId
        ? { ...t, name: editingCustomType.name.trim(), description: editingCustomType.description.trim(), group_name: editingCustomType.group_name.trim(), required: !!editingCustomType.required }
        : t);
      const res = await businessIntelligenceAPI.updateGroup(groupId, { custom_doc_types: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setEditingCustomType(null);
      showSuccessMsg('Document type updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingCustomTypeEdit(false);
    }
  };

  const handleToggleDocType = async (groupId, typeValue) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const excluded = [...(group.excluded_doc_types || [])];
    let priority = [...(group.doc_type_priority || [])];
    const isExcluded = excluded.includes(typeValue);
    const newExcluded = isExcluded
      ? excluded.filter(t => t !== typeValue)
      : [...excluded, typeValue];
    if (!isExcluded) priority = priority.filter(t => t !== typeValue);
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { excluded_doc_types: newExcluded, doc_type_priority: priority });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleMovePriority = async (groupId, typeValue, direction) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const excluded = group.excluded_doc_types || [];
    const customIds = (group.custom_doc_types || []).map(t => t.id);
    const allTypeValues = [...ARCHIVE_DOC_TYPES.map(t => t.value), ...customIds];
    const included = allTypeValues.filter(t => !excluded.includes(t));
    const saved = (group.doc_type_priority || []).filter(t => included.includes(t));
    const current = [...saved, ...included.filter(t => !saved.includes(t))];
    const idx = current.indexOf(typeValue);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? Math.max(0, idx - 1) : Math.min(current.length - 1, idx + 1);
    if (newIdx === idx) return;
    const reordered = [...current];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      const res = await businessIntelligenceAPI.updateGroup(groupId, { doc_type_priority: reordered });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleExpand = (id) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FiLoader className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (addFolderToGroup) {
    const addFolderGroup = groups.find(g => g.id === addFolderToGroup.groupId);
    return (
      <ArchiveFolderBrowser
        onAdd={handleAddMapping}
        onCancel={() => setAddFolderToGroup(null)}
        customDocTypes={addFolderGroup?.custom_doc_types || []}
      />
    );
  }

  if (pickingDestination) {
    return (
      <ArchiveFolderBrowser
        destinationMode
        onAdd={handleSetDestination}
        onCancel={() => setPickingDestination(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiArchive className="w-6 h-6 text-green-600" />
            Business Intelligence Module
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure groups and their source folder mappings
          </p>
        </div>
        <button
          onClick={handleToggleEnabled}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
            enabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {enabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
          {enabled ? 'Enabled' : 'Disabled'}
          {saving && <FiLoader className="w-4 h-4 animate-spin ml-1" />}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {!enabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          The Business Intelligence module is currently disabled. Enable it above to activate.
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200 -mb-2">
        {[
          { id: 'groups', label: 'Groups & Doc Types' },
          { id: 'scheduler', label: 'Server-Side Scheduler' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'scheduler' && <BISchedulerSettings />}

      {activeTab === 'groups' && <>
      {/* Business Intelligence Groups */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium text-gray-900">
            Business Intelligence Groups
            {groups.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">({groups.length})</span>
            )}
          </h3>
          <button
            onClick={() => { setShowNewGroupForm(true); setNewGroupName(''); setNewGroupDesc(''); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Group
          </button>
        </div>

        {/* New group inline form */}
        {showNewGroupForm && (
          <form onSubmit={handleCreateGroup} className="mb-4 border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-green-900">New Business Intelligence Group</h4>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Group Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                placeholder="e.g. Legal Documents"
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Description (optional)</label>
              <input
                type="text"
                value={newGroupDesc}
                onChange={e => setNewGroupDesc(e.target.value)}
                placeholder="e.g. Legal contracts and compliance documents"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNewGroupForm(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingGroup || !newGroupName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creatingGroup ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlus className="w-3.5 h-3.5" />}
                Create Group
              </button>
            </div>
          </form>
        )}

        {/* Group list */}
        {groups.length === 0 && !showNewGroupForm ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <FiFolder className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">No Business Intelligence groups yet</p>
            <p className="text-sm text-gray-500 mb-4">
              Create a group for each category of content and add their source folders to it.
            </p>
            <button
              onClick={() => setShowNewGroupForm(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Create First Group
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map(group => {
              const isExpanded = expandedGroups.has(group.id);
              const isEditing = editingGroup?.id === group.id;
              const mappings = group.folder_mappings || [];

              return (
                <div key={group.id} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Group header */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <button
                      onClick={() => toggleExpand(group.id)}
                      className="flex-1 flex items-center gap-2 text-left"
                    >
                      {isExpanded
                        ? <FiChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        : <FiChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      }
                      {isEditing ? (
                        <div className="flex-1 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editingGroup.name}
                            onChange={e => setEditingGroup(prev => ({ ...prev, name: e.target.value }))}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={editingGroup.description || ''}
                            onChange={e => setEditingGroup(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Description (optional)"
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                          />
                          <button
                            onClick={handleSaveGroupEdit}
                            disabled={editSaving}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {editSaving ? <FiLoader className="w-3 h-3 animate-spin" /> : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingGroup(null)}
                            className="px-2 py-1 text-gray-500 text-xs hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-gray-900">{group.name}</span>
                          {group.description && (
                            <span className="ml-2 text-xs text-gray-500">{group.description}</span>
                          )}
                        </div>
                      )}
                    </button>

                    {!isEditing && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-gray-500 mr-1">
                          {mappings.length} folder{mappings.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={() => handleToggleRfq(group)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                            group.rfq_enabled
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title={group.rfq_enabled ? 'RFQ stage enabled for this group' : 'RFQ stage disabled for this group'}
                        >
                          {group.rfq_enabled ? <FiToggleRight className="w-3.5 h-3.5" /> : <FiToggleLeft className="w-3.5 h-3.5" />}
                          RFQ
                        </button>
                        <button
                          onClick={() => setEditingGroup({ id: group.id, name: group.name, description: group.description || '' })}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors"
                          title="Edit group"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => { setAddFolderToGroup({ groupId: group.id }); setExpandedGroups(prev => new Set([...prev, group.id])); }}
                          className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50 transition-colors"
                          title="Add folder"
                        >
                          <FiPlus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteGroup(group)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                          title="Delete group"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded: folder mappings + doc type config + custom doc types */}
                  {isExpanded && (() => {
                    const customTypes = group.custom_doc_types || [];
                    const ctSt = getCustomTypeState(group.id);
                    return (
                      <div className="bg-white">
                        {mappings.length === 0 ? (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-gray-500 mb-3">No folders in this group yet.</p>
                            <button
                              onClick={() => setAddFolderToGroup({ groupId: group.id })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                            >
                              <FiPlus className="w-3.5 h-3.5" />
                              Add Folder
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Flat list — each mapping shows its type badges */}
                            {mappings.map(m => {
                              const typeList = m.types?.length ? m.types : (m.type ? [m.type] : []);
                              const isEditingThis = editingMappingTypes?.mappingId === m.id;
                              const allTypes = [
                                ...ARCHIVE_DOC_TYPES.map(dt => ({ value: dt.value, label: dt.label })),
                                ...customTypes.map(ct => ({ value: ct.id, label: ct.name, isCustom: true })),
                              ];
                              return (
                                <div key={m.id} className="border-t border-gray-100">
                                  <div className="flex items-center gap-3 px-4 py-2.5 group hover:bg-gray-50 transition-colors">
                                    <SourceIcon source={m.source} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-gray-800 truncate font-medium">
                                        {m.folder_name || m.mail_folder_name || 'Root'}
                                      </p>
                                      <p className="text-xs text-gray-400 truncate">{mappingPath(m)}</p>
                                    </div>
                                    {!isEditingThis && (
                                      <div className="flex items-center gap-1 flex-wrap justify-end flex-shrink-0 max-w-[40%]">
                                        {typeList.map(tv => {
                                          const predefined = ARCHIVE_DOC_TYPES.find(dt => dt.value === tv);
                                          const custom = customTypes.find(ct => ct.id === tv);
                                          const label = predefined?.label || custom?.name || tv;
                                          return (
                                            <span key={tv} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeColor(tv)}`}>
                                              {label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <button
                                      onClick={() => isEditingThis
                                        ? setEditingMappingTypes(null)
                                        : setEditingMappingTypes({ groupId: group.id, mappingId: m.id, types: [...typeList] })
                                      }
                                      className="opacity-0 group-hover:opacity-100 p-1 text-blue-400 hover:text-blue-600 transition-opacity flex-shrink-0"
                                      title={isEditingThis ? 'Cancel' : 'Edit types'}
                                    >
                                      {isEditingThis ? <FiX className="w-4 h-4" /> : <FiEdit2 className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => handleRemoveMapping(group.id, m.id)}
                                      className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity flex-shrink-0"
                                      title="Remove mapping"
                                    >
                                      <FiTrash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {isEditingThis && (
                                    <div className="px-4 pb-3 bg-blue-50 border-t border-blue-100">
                                      <p className="text-xs text-blue-700 font-medium py-2">Select document types for this folder:</p>
                                      <div className="grid grid-cols-2 gap-1 mb-2">
                                        {allTypes.map(dt => {
                                          const checked = editingMappingTypes.types.includes(dt.value);
                                          return (
                                            <button
                                              key={dt.value}
                                              onClick={() => setEditingMappingTypes(prev => ({
                                                ...prev,
                                                types: checked
                                                  ? prev.types.filter(v => v !== dt.value)
                                                  : [...prev.types, dt.value],
                                              }))}
                                              className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${
                                                checked ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                                              }`}
                                            >
                                              <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${checked ? 'border-white bg-white' : 'border-gray-300'}`}>
                                                {checked && <FiCheck className="w-2.5 h-2.5 text-blue-600" />}
                                              </div>
                                              <span className="truncate">{dt.label}</span>
                                              {dt.isCustom && <span className="ml-auto text-[10px] opacity-70 flex-shrink-0">custom</span>}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-blue-600">{editingMappingTypes.types.length} selected</span>
                                        <button
                                          onClick={handleSaveMappingTypes}
                                          disabled={editingMappingTypes.types.length === 0}
                                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                        >
                                          <FiCheck className="w-3.5 h-3.5" />
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="px-4 py-2 border-t border-gray-100 flex justify-end">
                              <button
                                onClick={() => setAddFolderToGroup({ groupId: group.id })}
                                className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition-colors"
                              >
                                <FiPlus className="w-3.5 h-3.5" />
                                Add Folder
                              </button>
                            </div>
                          </>
                        )}

                        {/* Doc Type Configuration — predefined + custom types */}
                        {(() => {
                          const excluded = group.excluded_doc_types || [];
                          const customTypes = group.custom_doc_types || [];
                          const allTypes = [
                            ...ARCHIVE_DOC_TYPES.map(t => ({ value: t.value, label: t.label, isCustom: false })),
                            ...customTypes.map(t => ({ value: t.id, label: t.name, isCustom: true, typeId: t.id, description: t.description, group_name: t.group_name, required: t.required })),
                          ];
                          const allValues = allTypes.map(t => t.value);
                          const included = allValues.filter(t => !excluded.includes(t));
                          const saved = (group.doc_type_priority || []).filter(t => included.includes(t));
                          const orderedIncluded = [...saved, ...included.filter(t => !saved.includes(t))];
                          return (
                            <div className="border-t border-gray-200 px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="text-xs font-medium text-gray-700">Doc Type Configuration</span>
                                  <span className="ml-1.5 text-xs text-gray-400">— set which types are used in Business Intelligence and their priority order</span>
                                </div>
                                {!ctSt.adding && (
                                  <button
                                    onClick={() => setCustomTypeField(group.id, 'adding', true)}
                                    className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition-colors"
                                  >
                                    <FiPlus className="w-3 h-3" />
                                    Add Type
                                  </button>
                                )}
                              </div>
                              <div className="space-y-1">
                                {allTypes.map(dt => {
                                  const isExcluded = excluded.includes(dt.value);
                                  const priorityIdx = orderedIncluded.indexOf(dt.value);
                                  const isEditingThisType = editingCustomType?.groupId === group.id && editingCustomType?.typeId === dt.typeId;
                                  if (isEditingThisType) {
                                    return (
                                      <div key={dt.value} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                                        <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                                          <input
                                            type="text"
                                            value={editingCustomType.name}
                                            onChange={e => setEditingCustomType(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Type name"
                                            autoFocus
                                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                          <input
                                            type="text"
                                            value={editingCustomType.description}
                                            onChange={e => setEditingCustomType(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Description"
                                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                          <input
                                            type="text"
                                            value={editingCustomType.group_name}
                                            onChange={e => setEditingCustomType(prev => ({ ...prev, group_name: e.target.value }))}
                                            placeholder="Group name (e.g. Technical)"
                                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                          <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <input
                                              type="checkbox"
                                              checked={!!editingCustomType.required}
                                              onChange={e => setEditingCustomType(prev => ({ ...prev, required: e.target.checked }))}
                                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            Required
                                          </label>
                                        </div>
                                        <button
                                          onClick={handleSaveCustomTypeEdit}
                                          disabled={savingCustomTypeEdit || !editingCustomType.name.trim()}
                                          className="p-1.5 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                                        >
                                          {savingCustomTypeEdit ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                        </button>
                                        <button
                                          onClick={() => setEditingCustomType(null)}
                                          className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                        >
                                          <FiX className="w-3 h-3" />
                                        </button>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={dt.value} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${isExcluded ? 'opacity-50 bg-gray-50' : 'bg-white'}`}>
                                      <span className={`w-5 text-center text-xs font-medium flex-shrink-0 ${isExcluded ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {isExcluded ? '—' : priorityIdx + 1}
                                      </span>
                                      <div className="flex flex-col gap-0 flex-shrink-0">
                                        <button
                                          disabled={isExcluded || priorityIdx === 0}
                                          onClick={() => handleMovePriority(group.id, dt.value, 'up')}
                                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-default"
                                          title="Move up"
                                        >
                                          <FiChevronUp className="w-3 h-3" />
                                        </button>
                                        <button
                                          disabled={isExcluded || priorityIdx === orderedIncluded.length - 1}
                                          onClick={() => handleMovePriority(group.id, dt.value, 'down')}
                                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-default"
                                          title="Move down"
                                        >
                                          <FiChevronDown className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${typeColor(dt.value)}`}>{dt.label}</span>
                                      {dt.isCustom && dt.group_name && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex-shrink-0">{dt.group_name}</span>
                                      )}
                                      {dt.isCustom && dt.required && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium flex-shrink-0">Required</span>
                                      )}
                                      {dt.isCustom && dt.description && (
                                        <span className="text-xs text-gray-400 truncate italic">{dt.description}</span>
                                      )}
                                      <span className="flex-1" />
                                      {dt.isCustom && (
                                        <button
                                          onClick={() => setEditingCustomType({ groupId: group.id, typeId: dt.typeId, name: dt.label, description: dt.description || '', group_name: dt.group_name || '', required: !!dt.required })}
                                          className="p-1 text-gray-300 hover:text-blue-500 transition-colors flex-shrink-0"
                                          title="Edit type"
                                        >
                                          <FiEdit2 className="w-3 h-3" />
                                        </button>
                                      )}
                                      {dt.isCustom && (
                                        <button
                                          onClick={() => handleDeleteCustomType(group.id, dt.typeId)}
                                          className="p-1 text-red-300 hover:text-red-500 transition-colors flex-shrink-0"
                                          title="Remove custom type"
                                        >
                                          <FiTrash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleToggleDocType(group.id, dt.value)}
                                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors flex-shrink-0 ${
                                          isExcluded
                                            ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                                            : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                        }`}
                                        title={isExcluded ? 'Click to include' : 'Click to exclude'}
                                      >
                                        {isExcluded
                                          ? <><FiX className="w-3 h-3" /> Excluded</>
                                          : <><FiCheck className="w-3 h-3" /> Included</>
                                        }
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>

                              {ctSt.adding && (
                                <div className="flex items-center gap-2 mt-2">
                                  <input
                                    type="text"
                                    value={ctSt.newName}
                                    onChange={e => setCustomTypeField(group.id, 'newName', e.target.value)}
                                    placeholder="Type name (e.g. COA)"
                                    autoFocus
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustomType(group.id); if (e.key === 'Escape') setCustomTypeField(group.id, 'adding', false); }}
                                  />
                                  <input
                                    type="text"
                                    value={ctSt.newDesc}
                                    onChange={e => setCustomTypeField(group.id, 'newDesc', e.target.value)}
                                    placeholder="Description (e.g. Certificate of Analysis)"
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustomType(group.id); if (e.key === 'Escape') setCustomTypeField(group.id, 'adding', false); }}
                                  />
                                  <input
                                    type="text"
                                    value={ctSt.newGroup || ''}
                                    onChange={e => setCustomTypeField(group.id, 'newGroup', e.target.value)}
                                    placeholder="Group name (e.g. Technical)"
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustomType(group.id); if (e.key === 'Escape') setCustomTypeField(group.id, 'adding', false); }}
                                  />
                                  <label className="flex items-center gap-1.5 text-xs text-gray-600 flex-shrink-0 px-1">
                                    <input
                                      type="checkbox"
                                      checked={!!ctSt.newRequired}
                                      onChange={e => setCustomTypeField(group.id, 'newRequired', e.target.checked)}
                                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    Required
                                  </label>
                                  <button
                                    onClick={() => handleAddCustomType(group.id)}
                                    disabled={ctSt.saving || !ctSt.newName.trim()}
                                    className="p-1.5 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                                  >
                                    {ctSt.saving ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                  </button>
                                  <button
                                    onClick={() => setCustomTypeState(prev => ({ ...prev, [group.id]: { adding: false, newName: '', newDesc: '', newGroup: '', newRequired: false, saving: false } }))}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                  >
                                    <FiX className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Archive Destination */}
                        <div className="border-t border-gray-200 px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="text-xs font-medium text-gray-700">Archive Destination</span>
                              <span className="ml-1.5 text-xs text-gray-400">— SharePoint folder where accepted documents are copied</span>
                            </div>
                          </div>
                          {group.archive_destination ? (
                            <div className="flex items-center gap-2">
                              <FiCloud className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 flex-1 truncate">
                                {group.archive_destination.folder_path || group.archive_destination.folder_name || 'SharePoint folder'}
                              </span>
                              <button
                                onClick={() => { setPickingDestination({ groupId: group.id }); setExpandedGroups(prev => new Set([...prev, group.id])); }}
                                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-0.5 rounded border border-blue-200 hover:bg-blue-50 transition-colors flex-shrink-0"
                              >
                                Change
                              </button>
                              <button
                                onClick={() => handleClearDestination(group.id)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                title="Remove destination"
                              >
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setPickingDestination({ groupId: group.id }); setExpandedGroups(prev => new Set([...prev, group.id])); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              <FiCloud className="w-3.5 h-3.5" />
                              Set Archive Destination
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Document Types */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-medium text-gray-900">Document Types</h3>
            <p className="text-xs text-gray-500 mt-0.5">Optional descriptions to help the AI identify each document type during extraction.</p>
          </div>
          {!editingDocTypes && (
            <button
              onClick={handleEditDocTypes}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {ARCHIVE_DOC_TYPES.map(t => (
            <div key={t.value} className="flex items-start gap-3 px-4 py-3">
              <span className={`mt-0.5 text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${typeColor(t.value)}`}>{t.label}</span>
              <div className="flex-1 min-w-0">
                {editingDocTypes ? (
                  <input
                    type="text"
                    value={docTypeEdits[t.value] || ''}
                    onChange={e => setDocTypeEdits(prev => ({ ...prev, [t.value]: e.target.value }))}
                    placeholder={DOC_TYPE_PLACEHOLDERS[t.value] || 'Describe this document type...'}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
                  />
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {docTypeDescriptions[t.value] || <span className="text-gray-300">No description set</span>}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {editingDocTypes && (
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={handleCancelDocTypes}
              disabled={savingDocTypes}
              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDocTypes}
              disabled={savingDocTypes}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {savingDocTypes ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-green-900 mb-2">About Business Intelligence Groups</h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Create one group per category of content (e.g. Legal, HR, Finance)</li>
          <li>• Each group holds the SharePoint, Exchange, and Doxolve folders for that category</li>
          <li>• Document types: {ARCHIVE_DOC_TYPES.map(t => t.label).join(', ')}</li>
          <li>• Custom types (per group): add extra types that appear in this group's documents</li>
        </ul>
      </div>

      {/* Delete group confirm modal */}
      {confirmDeleteGroup && (
        <DeleteGroupConfirm
          group={confirmDeleteGroup}
          onConfirm={handleDeleteGroup}
          onCancel={() => setConfirmDeleteGroup(null)}
          deleting={deletingGroupId === confirmDeleteGroup.id}
        />
      )}
      </>}
    </div>
  );
}
